import { Directive, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { VideoEntity, VideoLibraryEntity, VideoStreamingProviderEntity } from "../../../../../meta-data/app-meta-data.service";
import { VideoEvent } from "../../../models/video-event.model";
import { UpdateTimeResult } from "./models/update-time-result.model";
import { VideoEventType } from "../../../models/video-event-type.enum";
import { DwSecurityUserService } from "@devwareapps/devware-cap";
import { AppPermissions } from "../../../../../meta-data/app-permissions.enum";
import { VideoMessagingService } from "../../../services/video-messaging.service";
import { Subscription } from "rxjs";
import { DwPubSubService } from "../../../../z-devware/pubsub/dw-pub-sub-service";
import { VideoPlayback } from "../../../models/video-playback.model";

@Directive({
  selector: 'app-video-player-base'
})
export class VideoPlayerBaseComponent implements OnInit, OnDestroy, OnChanges {
  private readonly forcePreventSeekingQueryParam = 'forcePreventVideoSeeking';
  private readonly forcePreventSeekingSessionKey = 'aviator.video.forcePreventSeeking';

  @Input() video: VideoEntity;
  @Input() videoLibrary: VideoLibraryEntity;
  @Input() videoStreamingProvider: VideoStreamingProviderEntity;
  @Input() furthestWatchedTime: number;
  @Input() allowSeekOverride: boolean;
  @Input() preventSeekOverride: boolean;
  @Input() playback: VideoPlayback;

  @Output() videoWidthChanged = new EventEmitter<string>();
  @Output() initCompleted = new EventEmitter<boolean>();
  @Output() videoEvent = new EventEmitter<VideoEvent>();

  currentTime: number;
  videoDuration: number;
  userSeeked: boolean = false;

  allowSeek: boolean;

  subscriptions: Subscription[] = [];

  constructor(protected dwSecurityUserService: DwSecurityUserService, protected videoMessagingService: VideoMessagingService, protected dwPubSubService: DwPubSubService) {
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.subscriptions = [];
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.dwSecurityUserService.securityContextChanged().subscribe(() => {
        this.setupVideoSeek();
      }));

    this.setupVideoSeek();
    this.setupVideo();

    this.setupVideoEvents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.video || changes.videoStreamingProvider || changes.allowSeekOverride
      || changes.preventSeekOverride || changes.playback) {
      this.setupVideoSeek();
    }

    if (changes.video) {
      this.setupVideo();
    }
  }

  private setupVideoEvents() {
    this.subscriptions.push(
      this.videoMessagingService.getVideoPauseEvents().subscribe((message) => {
        this.dwPubSubService.emitDebug('Info', 'VPB - pause received', message);
        this.pauseVideo();
      }));
  }


  pauseVideo(): void {
    // Implement in derived classes
  }


  notifyVideoStarted() {
    //console.log('Notify Video Started');

    // Publish video started message to other tabs
    this.videoMessagingService.publishVideoStartedMessage(this.video?.VideoId);
  }


  setupVideo(): void {
    this.setupVideoSeek();
  }

  setupVideoSeek() {
    this.allowSeek = this.playback?.CanSeek ?? this.videoStreamingProvider?.AllowSeek;

    if (!this.allowSeek) {
      this.allowSeek = this.dwSecurityUserService.hasPermission(AppPermissions.videoCanSeek);
    }

    if (this.allowSeekOverride) {
      this.allowSeek = this.allowSeekOverride;
    }

    if (this.preventSeekOverride) {
      this.allowSeek = false;
    }

    if (this.shouldForcePreventSeeking()) {
      this.allowSeek = false;
    }
  }

  private shouldForcePreventSeeking(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      const queryValue = this.getForcePreventSeekingQueryValue();

      if (queryValue === 'true') {
        window.sessionStorage.setItem(this.forcePreventSeekingSessionKey, 'true');
      } else if (queryValue === 'false') {
        window.sessionStorage.removeItem(this.forcePreventSeekingSessionKey);
      }

      return window.sessionStorage.getItem(this.forcePreventSeekingSessionKey) === 'true';
    } catch {
      return false;
    }
  }

  private getForcePreventSeekingQueryValue(): string | null {
    const searchParams = new URLSearchParams(window.location.search);
    const searchValue = searchParams.get(this.forcePreventSeekingQueryParam);

    if (searchValue !== null) {
      return searchValue.toLowerCase();
    }

    const hashQueryIndex = window.location.hash.indexOf('?');
    if (hashQueryIndex < 0) {
      return null;
    }

    return new URLSearchParams(window.location.hash.substring(hashQueryIndex + 1))
      .get(this.forcePreventSeekingQueryParam)?.toLowerCase();
  }

  protected updateCurrentTime(timeSeconds: number, additionalSeekAllowanceSeconds: number = 0): UpdateTimeResult {
    this.currentTime = timeSeconds;

    const result: UpdateTimeResult = {
      userSeeked: false,
    }

    if (timeSeconds > this.furthestWatchedTime) {
      const changeSeconds = timeSeconds - this.furthestWatchedTime;

      const seekThresholdSeconds = (this.playback?.SeekThresholdSeconds
        || this.videoStreamingProvider?.SeekThreshholdSeconds
        || 5)
        + Math.max(additionalSeekAllowanceSeconds, 0);
      const belowSeekThreshhold = changeSeconds <= seekThresholdSeconds;

      if (belowSeekThreshhold) {
        this.userSeeked = false;
        this.furthestWatchedTime = timeSeconds;

        return result;
      }

      if (this.allowSeek) {
        this.furthestWatchedTime = timeSeconds;

        return result;
      }

      this.userSeeked = true;
      result.userSeeked = true;
      result.returnToPosition = this.furthestWatchedTime;
    }

    return result;
  }


  onVideoEvent(eventType: VideoEventType, data?: any) {
    const videoEvent: VideoEvent = {
      videoEventType: eventType,
      videoTime: this.currentTime,
      videoDuration: this.videoDuration,
      furtherestVideoTime: this.furthestWatchedTime,
      userSeeked: this.userSeeked,
      eventData: data
    };

    this.videoEvent.emit(videoEvent);

    switch (eventType) {
      case VideoEventType.videoStarted:
      case VideoEventType.videoPaused:
      case VideoEventType.videoCompleted:
      case VideoEventType.videoStopped:
      case VideoEventType.videoSeek:
        this.dwPubSubService.emitDebug('Info', `PJS Event ${eventType}`, data);
        break;
    }
  }

}


